/**
 * Enhanced Pagination Component
 *
 * Provides comprehensive pagination controls with:
 * - Page number input and jump-to-page functionality
 * - Previous/Next navigation
 * - First/Last page quick navigation
 * - Record count display ("Showing 1-50 of 1,234 employees")
 * - Current page indicator ("Page 3 of 25")
 * - Keyboard support (Enter key)
 * - Accessibility (ARIA labels)
 *
 * @example
 * ```tsx
 * <EnhancedPagination
 *   currentPage={3}
 *   totalPages={25}
 *   totalRecords={1234}
 *   pageSize={50}
 *   onPageChange={(page) => setCurrentPage(page)}
 * />
 * ```
 */

import React, { useState, useCallback, KeyboardEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react'

export interface EnhancedPaginationProps {
  /**
   * Current page number (1-indexed)
   */
  currentPage: number

  /**
   * Total number of pages
   */
  totalPages: number

  /**
   * Total number of records across all pages
   */
  totalRecords: number

  /**
   * Number of records per page
   */
  pageSize: number

  /**
   * Callback when page changes
   */
  onPageChange: (page: number) => void

  /**
   * Whether pagination is in loading state
   */
  loading?: boolean

  /**
   * Optional entity name for display (e.g., "colaboradores", "departamentos")
   * Defaults to "registros"
   */
  entityName?: string

  /**
   * Whether to show the "Ir para" (jump-to-page) input. Defaults to true.
   */
  showJumpToPage?: boolean

  /**
   * Optional callback when page size changes. When provided, a page size selector is rendered.
   */
  onPageSizeChange?: (size: number) => void

  /**
   * Options shown in the page size selector. Defaults to [10, 20, 50, 100].
   */
  pageSizeOptions?: number[]
}

/**
 * Enhanced Pagination Component
 */
export const EnhancedPagination: React.FC<EnhancedPaginationProps> = ({
  currentPage,
  totalPages,
  totalRecords,
  pageSize,
  onPageChange,
  loading = false,
  entityName = 'registros',
  showJumpToPage = true,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100]
}) => {
  const [jumpToPage, setJumpToPage] = useState('')

  /**
   * Calculate start and end record numbers for current page
   */
  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  /**
   * Handle jump to specific page
   */
  const handleJumpToPage = useCallback(() => {
    const pageNumber = parseInt(jumpToPage, 10)

    // Validate page number
    if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > totalPages) {
      // Invalid input - reset
      setJumpToPage('')
      return
    }

    // Navigate to page
    onPageChange(pageNumber)
    setJumpToPage('')
  }, [jumpToPage, totalPages, onPageChange])

  /**
   * Handle Enter key in jump to page input
   */
  const handleJumpToPageKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        handleJumpToPage()
      }
    },
    [handleJumpToPage]
  )

  /**
   * Navigate to first page
   */
  const goToFirstPage = useCallback(() => {
    if (currentPage !== 1) {
      onPageChange(1)
    }
  }, [currentPage, onPageChange])

  /**
   * Navigate to previous page
   */
  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
    }
  }, [currentPage, onPageChange])

  /**
   * Navigate to next page
   */
  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
    }
  }, [currentPage, totalPages, onPageChange])

  /**
   * Navigate to last page
   */
  const goToLastPage = useCallback(() => {
    if (currentPage !== totalPages) {
      onPageChange(totalPages)
    }
  }, [currentPage, totalPages, onPageChange])

  // Don't render if no pages
  if (totalPages === 0 || totalRecords === 0) {
    return null
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2 py-4">
      {/* Record Count Display + optional page size selector */}
      <div className="flex items-center gap-4">
        <div className="text-sm text-brand-gray">
          Mostrando{' '}
          <span className="font-medium text-white">
            {startRecord}-{endRecord}
          </span>{' '}
          de{' '}
          <span className="font-medium text-white">{totalRecords}</span>{' '}
          {entityName}
        </div>
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <label htmlFor="page-size" className="text-sm text-brand-gray">
              Por página:
            </label>
            <select
              id="page-size"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              disabled={loading}
              className="px-2 py-1 rounded-md border border-brand-dark-lighter bg-brand-dark-lighter text-white text-xs
                         cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center gap-2">
        {/* First Page Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={goToFirstPage}
          disabled={currentPage === 1 || loading}
          aria-label="Primeira página"
          title="Primeira página"
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>

        {/* Previous Page Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={goToPreviousPage}
          disabled={currentPage === 1 || loading}
          aria-label="Página anterior"
          title="Página anterior"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {/* Page Indicator */}
        <div className="flex items-center gap-2 text-sm text-brand-gray px-2">
          <span>
            Página{' '}
            <span className="font-medium text-white">{currentPage}</span> de{' '}
            <span className="font-medium text-white">{totalPages}</span>
          </span>
        </div>

        {/* Next Page Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={goToNextPage}
          disabled={currentPage === totalPages || loading}
          aria-label="Próxima página"
          title="Próxima página"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>

        {/* Last Page Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={goToLastPage}
          disabled={currentPage === totalPages || loading}
          aria-label="Última página"
          title="Última página"
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>

        {/* Jump to Page Input */}
        {showJumpToPage && totalPages > 1 && (
          <div className="flex items-center gap-2 ml-4">
            <label htmlFor="jump-to-page" className="text-sm text-brand-gray">
              Ir para:
            </label>
            <Input
              id="jump-to-page"
              type="number"
              min={1}
              max={totalPages}
              value={jumpToPage}
              onChange={(e) => setJumpToPage(e.target.value)}
              onKeyDown={handleJumpToPageKeyDown}
              placeholder="Pág."
              className="w-20 text-center"
              disabled={loading}
              aria-label={`Ir para página (1 a ${totalPages})`}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleJumpToPage}
              disabled={!jumpToPage || loading}
              aria-label="Ir para página"
            >
              Ir
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default EnhancedPagination
